import * as React from "react"
import { Smile } from "lucide-react"

import { InputGroupButton } from "@/components/ui/input-group"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  EMOJI_GROUPS,
  loadRecentEmoji,
  rememberEmoji,
  searchEmoji,
  type Emoji,
} from "@/lib/emoji"
import { cn } from "@/lib/utils"

type EmojiPickerProps = {
  onPick: (char: string) => void
  /**
   * What opens the picker. Defaults to the composer's smile button; a message
   * passes its own "add reaction" control. Gets `data-state` while open.
   */
  trigger?: React.ReactElement
  /** Closes after one pick — a reaction is one emoji, a message may be many. */
  closeOnPick?: boolean
  /** A row of go-to choices above the search, e.g. the common reactions. */
  quick?: readonly string[]
  side?: "top" | "bottom"
  align?: "start" | "center" | "end"
}

/**
 * The emoji picker, used by the composer and by a message's reaction control.
 *
 * It stays open while picking, so a row of emoji is a few taps rather than a
 * few round trips, and it never takes focus from the message field: the
 * caller keeps the caret and decides where the character lands. That is also
 * what keeps the on-screen keyboard up on a phone.
 */
export function EmojiPicker({
  onPick,
  trigger,
  closeOnPick = false,
  quick,
  side = "top",
  align = "start",
}: EmojiPickerProps) {
  const [open, setOpen] = React.useState(false)

  function pick(char: string) {
    onPick(char)
    if (closeOnPick) setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          trigger ?? (
            <InputGroupButton size="icon-sm" aria-label="Эмодзи">
              <Smile />
            </InputGroupButton>
          )
        }
        data-state={open ? "open" : "closed"}
      />

      <PopoverContent
        align={align}
        side={side}
        className="w-[min(20rem,calc(100vw-1.5rem))] gap-2 p-2"
        // Without this the popup grabs focus as it opens, which on a phone
        // drops the keyboard and loses the caret the emoji is going into.
        initialFocus={false}
      >
        <EmojiPanel onPick={pick} quick={quick} />
      </PopoverContent>
    </Popover>
  )
}

/**
 * The picker's body: an optional row of go-to choices, search, recents and
 * the catalogue. Mounted only while shown, so recents are read fresh each
 * time — another tab may have used something since — and the search starts
 * empty.
 */
export function EmojiPanel({
  onPick,
  quick,
  className,
}: {
  onPick: (char: string) => void
  quick?: readonly string[]
  className?: string
}) {
  const [query, setQuery] = React.useState("")
  const [recent, setRecent] = React.useState<Emoji[]>(loadRecentEmoji)

  function pick(char: string) {
    onPick(char)
    setRecent(rememberEmoji(char))
  }

  const found = searchEmoji(query)
  const searching = query.trim() !== ""

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", className)}>
      {quick && quick.length > 0 && !searching && (
        <Grid
          emoji={quick.map((char) => ({ char, keywords: char }))}
          onPick={pick}
          className="grid-cols-8 border-b pb-2"
        />
      )}

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Поиск эмодзи"
        aria-label="Поиск эмодзи"
        className="h-8"
      />

      <div className="no-scrollbar flex max-h-64 min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain">
        {searching ? (
          found.length > 0 ? (
            <Grid emoji={found} onPick={pick} />
          ) : (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              Ничего не нашлось
            </p>
          )
        ) : (
          <>
            {recent.length > 0 && (
              <Section name="Недавние" emoji={recent} onPick={pick} />
            )}
            {EMOJI_GROUPS.map((group) => (
              <Section
                key={group.name}
                name={group.name}
                emoji={group.emoji}
                onPick={pick}
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function Section({
  name,
  emoji,
  onPick,
}: {
  name: string
  emoji: Emoji[]
  onPick: (char: string) => void
}) {
  return (
    <section>
      <h3 className="sticky top-0 bg-popover pb-1 text-[11px] font-medium text-muted-foreground">
        {name}
      </h3>
      <Grid emoji={emoji} onPick={onPick} />
    </section>
  )
}

function Grid({
  emoji,
  onPick,
  className,
}: {
  emoji: Emoji[]
  onPick: (char: string) => void
  className?: string
}) {
  return (
    <div className={cn("grid grid-cols-7 gap-0.5", className)}>
      {emoji.map((item) => (
        <button
          key={item.char}
          type="button"
          // A press must not move focus out of the message field; the click
          // still fires, and the caret stays where the character goes.
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => onPick(item.char)}
          title={item.keywords}
          aria-label={item.keywords}
          className="flex aspect-square items-center justify-center rounded-md text-xl leading-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {item.char}
        </button>
      ))}
    </div>
  )
}
